import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, Upload, Trash2, Image as ImageIcon, Save, Building2, Phone, Globe, Mail, MapPin, Clock, MessageSquare, Users, User, Plus } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import Header from '../components/Header';
import MainTabsNav from '../components/MainTabsNav';
import Footer from '../components/Footer';
import AdminSidebar from '../components/AdminSidebar';
import BannerImageCropModal from '../components/BannerImageCropModal';
import InstructorImageCropModal from '../components/InstructorImageCropModal';
import RichTextEditor from '../components/RichTextEditor';

interface Announcement {
  id: string;
  provider_id: string;
  title: string;
  content: string;
  is_pinned: boolean;
  created_at: string;
  updated_at: string;
}

interface Instructor {
  id: string;
  provider_id: string;
  name: string;
  description: string;
  image_url: string | null;
  display_order: number;
}

export default function ProviderPremiumPage() {
  const { user, isAuthenticated, checkSession } = useAuthStore();
  const navigate = useNavigate();
  const [bannerUrl, setBannerUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  // Provider information form fields
  const [providerInfo, setProviderInfo] = useState({
    description: '',
    phone: '',
    website: '',
    email: '',
    address: '',
    operating_hours: '',
  });

  // Tab management
  const [activeTab, setActiveTab] = useState<'info' | 'announcements' | 'instructors'>('info');

  // Announcement management
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [announcementsLoading, setAnnouncementsLoading] = useState(true);
  const [showAnnouncementForm, setShowAnnouncementForm] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
  const [announcementTitle, setAnnouncementTitle] = useState('');
  const [announcementContent, setAnnouncementContent] = useState('');
  const [announcementPinned, setAnnouncementPinned] = useState(false);

  // Instructor management
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [instructorsLoading, setInstructorsLoading] = useState(true);
  const [showInstructorForm, setShowInstructorForm] = useState(false);
  const [editingInstructor, setEditingInstructor] = useState<Instructor | null>(null);
  const [instructorName, setInstructorName] = useState('');
  const [instructorDescription, setInstructorDescription] = useState('');
  const [instructorImageUrl, setInstructorImageUrl] = useState('');
  const [instructorImageBlob, setInstructorImageBlob] = useState<Blob | null>(null);
  const [showInstructorCropModal, setShowInstructorCropModal] = useState(false);
  const [uploadingInstructorImage, setUploadingInstructorImage] = useState(false);

  // 비로그인 시 리다이렉트 및 페이지 진입 시 사용자 정보 갱신
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }
    // 페이지 진입 시 항상 최신 사용자 정보 확인
    checkSession();
  }, [isAuthenticated, navigate, checkSession]);

  // 사용자 배너 URL 및 프로바이더 정보 로드
  useEffect(() => {
    if (user?.banner_image_url) {
      setBannerUrl(user.banner_image_url);
    }
    // Load provider info from user metadata
    if (user) {
      setProviderInfo({
        description: (user as any).description || '',
        phone: (user as any).phone || '',
        website: (user as any).website || '',
        email: (user as any).email || user.email || '',
        address: (user as any).address || '',
        operating_hours: (user as any).operating_hours || '',
      });
    }
  }, [user]);

  // 공지사항 로드
  useEffect(() => {
    const fetchAnnouncements = async () => {
      if (!user?.id) return;

      try {
        setAnnouncementsLoading(true);
        const response = await fetch(`/api/v1/providers/${user.id}/announcements`, {
          credentials: 'include',
        });

        if (!response.ok) {
          console.error('Failed to fetch announcements');
          setAnnouncements([]);
          return;
        }

        const data = await response.json();
        setAnnouncements(data.announcements || []);
      } catch (err) {
        console.error('Failed to fetch announcements:', err);
        setAnnouncements([]);
      } finally {
        setAnnouncementsLoading(false);
      }
    };

    if (user) {
      fetchAnnouncements();
    }
  }, [user]);

  // 강사 로드
  useEffect(() => {
    const fetchInstructors = async () => {
      if (!user?.id) return;

      try {
        setInstructorsLoading(true);
        const response = await fetch(`/api/v1/providers/${user.id}/instructors`, {
          credentials: 'include',
        });

        if (!response.ok) {
          console.error('Failed to fetch instructors');
          setInstructors([]);
          return;
        }

        const data = await response.json();
        setInstructors(data.instructors || []);
      } catch (err) {
        console.error('Failed to fetch instructors:', err);
        setInstructors([]);
      } finally {
        setInstructorsLoading(false);
      }
    };

    if (user) {
      fetchInstructors();
    }
  }, [user]);

  if (!isAuthenticated) {
    return null;
  }

  // 이미지 파일 선택
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // 파일 타입 검증
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: '이미지 파일만 업로드할 수 있습니다.' });
      return;
    }

    // 파일 크기 검증 (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: '이미지 크기는 5MB 이하여야 합니다.' });
      return;
    }

    // 이미지 미리보기 및 크롭 모달 열기
    const reader = new FileReader();
    reader.onload = (e) => {
      const imageUrl = e.target?.result as string;
      setSelectedImage(imageUrl);
      setCropModalOpen(true);
    };
    reader.readAsDataURL(file);
  };

  // 크롭된 이미지 업로드
  const handleSaveCroppedImage = async (croppedBlob: Blob) => {
    setUploading(true);
    setMessage(null);

    try {
      const formData = new FormData();
      formData.append('image', croppedBlob, 'banner.jpg');

      const response = await fetch('/api/v1/provider/banner', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || '배너 이미지 업로드에 실패했습니다.');
      }

      setBannerUrl(data.imageUrl);
      setMessage({ type: 'success', text: '배너 이미지가 업로드되었습니다.' });
      setCropModalOpen(false);
      setSelectedImage(null);

      // authStore 세션 재확인 (배너 URL 업데이트)
      await useAuthStore.getState().checkSession();
    } catch (error) {
      console.error('Upload error:', error);
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : '업로드에 실패했습니다.',
      });
    } finally {
      setUploading(false);
    }
  };

  // 배너 이미지 삭제
  const handleDeleteBanner = async () => {
    if (!confirm('배너 이미지를 삭제하시겠습니까?')) {
      return;
    }

    setDeleting(true);
    setMessage(null);

    try {
      const response = await fetch('/api/v1/provider/banner', {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || '배너 이미지 삭제에 실패했습니다.');
      }

      setBannerUrl(null);
      setMessage({ type: 'success', text: '배너 이미지가 삭제되었습니다.' });

      // authStore 세션 재확인
      await useAuthStore.getState().checkSession();
    } catch (error) {
      console.error('Delete error:', error);
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : '삭제에 실패했습니다.',
      });
    } finally {
      setDeleting(false);
    }
  };

  // Provider information 저장
  const handleSaveProviderInfo = async () => {
    setSaving(true);
    setMessage(null);

    try {
      const response = await fetch('/api/v1/provider/info', {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(providerInfo),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || '업체 정보 저장에 실패했습니다.');
      }

      setMessage({ type: 'success', text: '업체 정보가 저장되었습니다.' });

      // authStore 세션 재확인
      await useAuthStore.getState().checkSession();
    } catch (error) {
      console.error('Save error:', error);
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : '저장에 실패했습니다.',
      });
    } finally {
      setSaving(false);
    }
  };

  // 공지사항 생성/수정 핸들러
  const handleSaveAnnouncement = async () => {
    if (!announcementTitle.trim() || !announcementContent.trim()) {
      setMessage({ type: 'error', text: '제목과 내용을 입력해주세요.' });
      return;
    }

    try {
      const url = editingAnnouncement
        ? `/api/v1/announcements/${editingAnnouncement.id}`
        : '/api/v1/announcements';

      const method = editingAnnouncement ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: announcementTitle,
          content: announcementContent,
          is_pinned: announcementPinned,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save announcement');
      }

      // 목록 새로고침
      const listResponse = await fetch(`/api/v1/providers/${user?.id}/announcements`, {
        credentials: 'include',
      });
      const data = await listResponse.json();
      setAnnouncements(data.announcements || []);

      // 폼 초기화
      setShowAnnouncementForm(false);
      setEditingAnnouncement(null);
      setAnnouncementTitle('');
      setAnnouncementContent('');
      setAnnouncementPinned(false);
      setMessage({ type: 'success', text: editingAnnouncement ? '공지사항이 수정되었습니다.' : '공지사항이 등록되었습니다.' });
    } catch (error: any) {
      console.error('Failed to save announcement:', error);
      setMessage({ type: 'error', text: '공지사항 저장에 실패했습니다.' });
    }
  };

  // 공지사항 삭제 핸들러
  const handleDeleteAnnouncement = async (id: string) => {
    if (!confirm('정말 삭제하시겠습니까?')) return;

    try {
      const response = await fetch(`/api/v1/announcements/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to delete announcement');
      }

      setAnnouncements(prev => prev.filter(a => a.id !== id));
      setMessage({ type: 'success', text: '공지사항이 삭제되었습니다.' });
    } catch (error: any) {
      console.error('Failed to delete announcement:', error);
      setMessage({ type: 'error', text: '공지사항 삭제에 실패했습니다.' });
    }
  };

  // 수정 모드로 전환
  const handleEditAnnouncement = (announcement: Announcement) => {
    setEditingAnnouncement(announcement);
    setAnnouncementTitle(announcement.title);
    setAnnouncementContent(announcement.content);
    setAnnouncementPinned(announcement.is_pinned);
    setShowAnnouncementForm(true);
  };

  // 강사 생성/수정 핸들러
  const handleSaveInstructor = async () => {
    if (!instructorName.trim()) {
      setMessage({ type: 'error', text: '강사 이름을 입력해주세요.' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    try {
      const url = editingInstructor
        ? `/api/v1/instructors/${editingInstructor.id}`
        : '/api/v1/instructors';

      const method = editingInstructor ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: instructorName,
          description: instructorDescription,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save instructor');
      }

      const savedInstructor = await response.json();
      const instructorId = savedInstructor.instructor?.id || editingInstructor?.id;

      // 이미지가 있으면 업로드
      if (instructorImageBlob && instructorId) {
        const formData = new FormData();
        formData.append('image', instructorImageBlob, 'profile.png');

        const imageResponse = await fetch(`/api/v1/instructors/${instructorId}/image`, {
          method: 'POST',
          credentials: 'include',
          body: formData,
        });

        if (!imageResponse.ok) {
          console.error('Failed to upload image');
        }
      }

      // 목록 새로고침
      const listResponse = await fetch(`/api/v1/providers/${user?.id}/instructors`, {
        credentials: 'include',
      });
      const data = await listResponse.json();
      setInstructors(data.instructors || []);

      // 폼 초기화
      setShowInstructorForm(false);
      setEditingInstructor(null);
      setInstructorName('');
      setInstructorDescription('');
      setInstructorImageUrl('');
      setInstructorImageBlob(null);
      setMessage({ type: 'success', text: editingInstructor ? '강사 정보가 수정되었습니다.' : '강사가 등록되었습니다.' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      console.error('Failed to save instructor:', error);
      setMessage({ type: 'error', text: '강사 저장에 실패했습니다.' });
      setTimeout(() => setMessage(null), 3000);
    }
  };

  // 강사 삭제 핸들러
  const handleDeleteInstructor = async (id: string) => {
    if (!confirm('정말 삭제하시겠습니까?')) return;

    try {
      const response = await fetch(`/api/v1/instructors/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to delete instructor');
      }

      setInstructors(prev => prev.filter(i => i.id !== id));
      setMessage({ type: 'success', text: '강사가 삭제되었습니다.' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      console.error('Failed to delete instructor:', error);
      setMessage({ type: 'error', text: '강사 삭제에 실패했습니다.' });
      setTimeout(() => setMessage(null), 3000);
    }
  };

  // 강사 수정 모드로 전환
  const handleEditInstructor = (instructor: Instructor) => {
    setEditingInstructor(instructor);
    setInstructorName(instructor.name);
    setInstructorDescription(instructor.description || '');
    setInstructorImageUrl(instructor.image_url || '');
    setInstructorImageBlob(null); // 수정 모드에서는 기존 이미지 사용
    setShowInstructorForm(true);
  };

  // 강사 이미지 선택
  const handleInstructorImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: '이미지 파일만 업로드할 수 있습니다.' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: '이미지 크기는 5MB 이하여야 합니다.' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setInstructorImageUrl(reader.result as string);
      setShowInstructorCropModal(true);
    };
    reader.readAsDataURL(file);
  };

  // 크롭된 강사 이미지 저장 (임시 저장)
  const handleSaveCroppedInstructorImage = async (croppedImageBlob: Blob) => {
    // Blob을 URL로 변환하여 미리보기 표시
    const blobUrl = URL.createObjectURL(croppedImageBlob);
    setInstructorImageUrl(blobUrl);
    setInstructorImageBlob(croppedImageBlob);
    setShowInstructorCropModal(false);
  };

  // 강사 이미지 삭제
  const handleDeleteInstructorImage = async () => {
    if (!confirm('정말 삭제하시겠습니까?')) return;

    // 기존 강사의 저장된 이미지 삭제
    if (editingInstructor && editingInstructor.image_url) {
      try {
        const response = await fetch(`/api/v1/instructors/${editingInstructor.id}/image`, {
          method: 'DELETE',
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error('Failed to delete image');
        }

        // 목록 새로고침
        const listResponse = await fetch(`/api/v1/providers/${user?.id}/instructors`, {
          credentials: 'include',
        });
        const data = await listResponse.json();
        setInstructors(data.instructors || []);

        setMessage({ type: 'success', text: '프로필 이미지가 삭제되었습니다.' });
        setTimeout(() => setMessage(null), 3000);
      } catch (error: any) {
        console.error('Failed to delete image:', error);
        setMessage({ type: 'error', text: '이미지 삭제에 실패했습니다.' });
        setTimeout(() => setMessage(null), 3000);
      }
    }

    // 임시 이미지 제거
    setInstructorImageUrl('');
    setInstructorImageBlob(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <MainTabsNav className="mt-16" />
      <div className="flex flex-1 max-w-[1440px] mx-auto w-full">
        {/* 공유 사이드바 */}
        <AdminSidebar />

        {/* 메인 콘텐츠 */}
        <main className="flex-1 p-4 relative">
          <div>
            {/* 인증 우수 비활성화 오버레이 */}
            {!user?.is_premium && (
              <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-sm z-40 flex items-center justify-center">
                <div className="bg-white rounded-xl shadow-xl p-8 max-w-md mx-4 text-center">
                  <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Crown className="w-8 h-8 text-amber-500" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    우수 인증 교육 기관 전용
                  </h2>
                  <p className="text-gray-600 mb-6">
                    이 페이지의 정보를 수정하려면 우수 인증 교육 기관 승인이 필요합니다.
                    <br />
                    관리자의 승인을 기다려주세요.
                  </p>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="text-sm text-amber-800">
                      승인이 완료되면 메시지함으로 알림이 발송됩니다.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* 헤더 */}
            <div className="mb-4">
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Crown className="w-6 h-6 text-amber-500" />
                우수 인증 교육 기관 관리
              </h1>
              <p className="text-gray-500 mt-1">
                배너 이미지 및 업체 정보를 관리하세요.
              </p>
            </div>

            {/* 메시지 */}
            {message && (
              <div
                className={`mb-4 p-3 rounded-lg ${
                  message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                }`}
              >
                {message.text}
              </div>
            )}

            {/* 탭 메뉴 */}
            <div className="mb-6 border-b border-gray-200">
              <nav className="flex space-x-8">
                <button
                  onClick={() => setActiveTab('info')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'info'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    업체 정보
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('announcements')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'announcements'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    공지사항 관리
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('instructors')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'instructors'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    강사 관리
                  </div>
                </button>
              </nav>
            </div>

            {/* 업체 정보 탭 */}
            {activeTab === 'info' && (
            <div className="grid grid-cols-1 lg:grid-cols-[3fr_4fr] gap-4">
              {/* 배너 이미지 영역 */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <ImageIcon className="w-5 h-5 text-primary" />
                  배너 이미지
                </h2>

                {/* 현재 배너 미리보기 */}
                {bannerUrl ? (
                  <div className="space-y-3">
                    <div className="relative aspect-video rounded-lg overflow-hidden bg-gray-100">
                      <img
                        src={bannerUrl}
                        alt="배너 이미지"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex gap-2">
                      <label
                        htmlFor="banner-upload"
                        className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center justify-center gap-2 cursor-pointer text-sm"
                      >
                        <Upload className="w-4 h-4" />
                        배너 변경
                      </label>
                      <button
                        onClick={handleDeleteBanner}
                        disabled={deleting}
                        className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center gap-2 disabled:opacity-50 text-sm"
                      >
                        {deleting ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            삭제 중...
                          </>
                        ) : (
                          <>
                            <Trash2 className="w-4 h-4" />
                            삭제
                          </>
                        )}
                      </button>
                    </div>
                    {/* 배너 안내 */}
                    <div className="p-2 bg-amber-50 rounded-lg border border-amber-200">
                      <p className="text-xs text-amber-700">
                        메인 페이지 "우수 인증 교육 기관"에 노출 | 권장: 800 x 450px (16:9), 최대 5MB
                      </p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <label
                      htmlFor="banner-upload"
                      className="block w-full aspect-video border-2 border-dashed border-gray-300 rounded-lg hover:border-primary transition-colors cursor-pointer"
                    >
                      <div className="flex flex-col items-center justify-center h-full text-gray-500 hover:text-primary">
                        <ImageIcon className="w-12 h-12 mb-2" />
                        <p className="font-medium text-sm">배너 이미지 업로드</p>
                        <p className="text-xs mt-1">800 x 450px | 5MB</p>
                      </div>
                    </label>
                    {/* 배너 안내 */}
                    <div className="mt-3 p-2 bg-amber-50 rounded-lg border border-amber-200">
                      <p className="text-xs text-amber-700">
                        메인 페이지 "우수 인증 교육 기관"에 노출 | 권장: 800 x 450px (16:9), 최대 5MB
                      </p>
                    </div>
                  </div>
                )}

                <input
                  id="banner-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>

              {/* 업체 정보 영역 */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-primary" />
                  업체 정보
                </h2>

                <div className="space-y-3">
                  {/* 업체 소개 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      업체 소개
                    </label>
                    <RichTextEditor
                      content={providerInfo.description}
                      onChange={(html) => setProviderInfo({ ...providerInfo, description: html })}
                      placeholder="업체에 대한 간단한 소개를 입력하세요"
                      minHeight="180px"
                    />
                  </div>

                  {/* 연락처 정보 */}
                  <div className="grid grid-cols-1 gap-3">
                    {/* 전화번호 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                        <Phone className="w-4 h-4" />
                        센터 전화번호
                      </label>
                      <input
                        type="tel"
                        value={providerInfo.phone}
                        onChange={(e) => setProviderInfo({ ...providerInfo, phone: e.target.value })}
                        placeholder="010-1234-5678"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none text-sm"
                      />
                    </div>

                    {/* 이메일 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        이메일
                      </label>
                      <input
                        type="email"
                        value={providerInfo.email}
                        onChange={(e) => setProviderInfo({ ...providerInfo, email: e.target.value })}
                        placeholder="contact@example.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none text-sm"
                      />
                    </div>

                    {/* 홈페이지 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                        <Globe className="w-4 h-4" />
                        홈페이지
                      </label>
                      <input
                        type="url"
                        value={providerInfo.website}
                        onChange={(e) => setProviderInfo({ ...providerInfo, website: e.target.value })}
                        placeholder="https://example.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none text-sm"
                      />
                    </div>

                    {/* 주소 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        주소
                      </label>
                      <input
                        type="text"
                        value={providerInfo.address}
                        onChange={(e) => setProviderInfo({ ...providerInfo, address: e.target.value })}
                        placeholder="서울특별시 강남구..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none text-sm"
                      />
                    </div>

                    {/* 운영 시간 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        운영 시간
                      </label>
                      <input
                        type="text"
                        value={providerInfo.operating_hours}
                        onChange={(e) => setProviderInfo({ ...providerInfo, operating_hours: e.target.value })}
                        placeholder="평일 09:00 - 22:00"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none text-sm"
                      />
                    </div>
                  </div>

                  {/* 저장 버튼 */}
                  <button
                    onClick={handleSaveProviderInfo}
                    disabled={saving}
                    className="w-full px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 text-sm font-medium"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        저장 중...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        업체 정보 저장
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
            )}

            {/* 공지사항 관리 탭 */}
            {activeTab === 'announcements' && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-gray-900">공지사항 관리</h2>
                  <button
                    onClick={() => {
                      setShowAnnouncementForm(true);
                      setEditingAnnouncement(null);
                      setAnnouncementTitle('');
                      setAnnouncementContent('');
                      setAnnouncementPinned(false);
                    }}
                    className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center gap-2 text-sm"
                  >
                    <MessageSquare className="w-4 h-4" />
                    새 공지사항 작성
                  </button>
                </div>

                {/* 공지사항 작성/수정 폼 */}
                {showAnnouncementForm && (
                  <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
                    <h3 className="font-semibold text-gray-900 mb-4">
                      {editingAnnouncement ? '공지사항 수정' : '새 공지사항'}
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          제목 *
                        </label>
                        <input
                          type="text"
                          value={announcementTitle}
                          onChange={(e) => setAnnouncementTitle(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                          placeholder="공지사항 제목을 입력하세요"
                          maxLength={200}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          내용 *
                        </label>
                        <RichTextEditor
                          content={announcementContent}
                          onChange={setAnnouncementContent}
                          placeholder="공지사항 내용을 입력하세요"
                        />
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="pinned"
                          checked={announcementPinned}
                          onChange={(e) => setAnnouncementPinned(e.target.checked)}
                          className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                        />
                        <label htmlFor="pinned" className="ml-2 text-sm text-gray-700">
                          상단 고정
                        </label>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={handleSaveAnnouncement}
                          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors text-sm"
                        >
                          {editingAnnouncement ? '수정' : '등록'}
                        </button>
                        <button
                          onClick={() => {
                            setShowAnnouncementForm(false);
                            setEditingAnnouncement(null);
                            setAnnouncementTitle('');
                            setAnnouncementContent('');
                            setAnnouncementPinned(false);
                          }}
                          className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors text-sm"
                        >
                          취소
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* 공지사항 목록 */}
                {announcementsLoading ? (
                  <div className="animate-pulse space-y-4">
                    <div className="h-24 bg-gray-200 rounded"></div>
                    <div className="h-24 bg-gray-200 rounded"></div>
                  </div>
                ) : announcements.length > 0 ? (
                  <div className="space-y-4">
                    {announcements.map((announcement) => (
                      <div
                        key={announcement.id}
                        className="border border-gray-200 rounded-lg p-4 hover:border-primary transition-colors"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                            {announcement.is_pinned && (
                              <span className="text-accent">📌</span>
                            )}
                            {announcement.title}
                          </h3>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEditAnnouncement(announcement)}
                              className="text-sm text-primary hover:underline"
                            >
                              수정
                            </button>
                            <button
                              onClick={() => handleDeleteAnnouncement(announcement.id)}
                              className="text-sm text-red-600 hover:underline"
                            >
                              삭제
                            </button>
                          </div>
                        </div>
                        <p className="text-gray-700 text-sm whitespace-pre-line line-clamp-2">
                          {announcement.content.replace(/<[^>]*>/g, '')}
                        </p>
                        <p className="text-xs text-gray-500 mt-2">
                          {new Date(announcement.created_at).toLocaleString('ko-KR')}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    등록된 공지사항이 없습니다.
                  </div>
                )}
              </div>
            )}

            {/* 강사 관리 탭 */}
            {activeTab === 'instructors' && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-gray-900">강사 관리</h2>
                  <button
                    onClick={() => {
                      setShowInstructorForm(true);
                      setEditingInstructor(null);
                      setInstructorName('');
                      setInstructorDescription('');
                      setInstructorImageUrl('');
                      setInstructorImageBlob(null);
                    }}
                    className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center gap-2 text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    새 강사 추가
                  </button>
                </div>

                {/* 강사 작성/수정 폼 */}
                {showInstructorForm && (
                  <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
                    <h3 className="font-semibold text-gray-900 mb-4">
                      {editingInstructor ? '강사 수정' : '새 강사'}
                    </h3>
                    <div className="space-y-4">
                      {/* 프로필 이미지 */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          프로필 이미지
                        </label>
                        <div className="flex items-center gap-4">
                          {instructorImageUrl ? (
                            <div className="relative">
                              <img
                                src={instructorImageUrl}
                                alt="Preview"
                                className="w-24 h-24 rounded-full object-cover"
                              />
                              <button
                                onClick={handleDeleteInstructorImage}
                                className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center">
                              <User className="w-12 h-12 text-gray-400" />
                            </div>
                          )}
                          <label className="cursor-pointer px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleInstructorImageSelect}
                              className="hidden"
                            />
                            이미지 선택
                          </label>
                        </div>
                      </div>

                      {/* 이름 */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          이름 *
                        </label>
                        <input
                          type="text"
                          value={instructorName}
                          onChange={(e) => setInstructorName(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                          placeholder="강사 이름을 입력하세요"
                          maxLength={100}
                        />
                      </div>

                      {/* 소개 */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          소개
                        </label>
                        <RichTextEditor
                          content={instructorDescription}
                          onChange={(html) => setInstructorDescription(html)}
                          placeholder="강사 소개를 입력하세요"
                        />
                      </div>

                      {/* 버튼 */}
                      <div className="flex gap-2">
                        <button
                          onClick={handleSaveInstructor}
                          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors text-sm"
                        >
                          {editingInstructor ? '수정' : '등록'}
                        </button>
                        <button
                          onClick={() => {
                            setShowInstructorForm(false);
                            setEditingInstructor(null);
                            setInstructorName('');
                            setInstructorDescription('');
                            setInstructorImageUrl('');
                            setInstructorImageBlob(null);
                          }}
                          className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors text-sm"
                        >
                          취소
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* 강사 목록 */}
                {instructorsLoading ? (
                  <div className="animate-pulse space-y-4">
                    <div className="h-24 bg-gray-200 rounded"></div>
                    <div className="h-24 bg-gray-200 rounded"></div>
                  </div>
                ) : instructors.length > 0 ? (
                  <div className="space-y-4">
                    {instructors.map((instructor) => (
                      <div
                        key={instructor.id}
                        className="border border-gray-200 rounded-lg p-4 hover:border-primary transition-colors"
                      >
                        <div className="flex items-start gap-4">
                          {/* 프로필 이미지 */}
                          {instructor.image_url ? (
                            <img
                              src={instructor.image_url}
                              alt={instructor.name}
                              className="w-16 h-16 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center">
                              <User className="w-8 h-8 text-gray-400" />
                            </div>
                          )}

                          {/* 정보 */}
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900">
                              {instructor.name}
                            </h3>
                            {instructor.description && (
                              <div
                                className="tiptap prose prose-sm max-w-none text-sm text-gray-700 mt-1 line-clamp-2"
                                dangerouslySetInnerHTML={{ __html: instructor.description }}
                              />
                            )}
                          </div>

                          {/* 액션 버튼 */}
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEditInstructor(instructor)}
                              className="text-sm text-primary hover:underline"
                            >
                              수정
                            </button>
                            <button
                              onClick={() => handleDeleteInstructor(instructor.id)}
                              className="text-sm text-red-600 hover:underline"
                            >
                              삭제
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    등록된 강사가 없습니다.
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>
      <Footer />

      {/* 배너 크롭 모달 */}
      {cropModalOpen && selectedImage && (
        <BannerImageCropModal
          imageUrl={selectedImage}
          onClose={() => {
            setCropModalOpen(false);
            setSelectedImage(null);
          }}
          onSave={handleSaveCroppedImage}
          isSaving={uploading}
        />
      )}

      {/* 강사 이미지 크롭 모달 */}
      {showInstructorCropModal && instructorImageUrl && (
        <InstructorImageCropModal
          imageUrl={instructorImageUrl}
          onClose={() => setShowInstructorCropModal(false)}
          onSave={handleSaveCroppedInstructorImage}
          isSaving={uploadingInstructorImage}
        />
      )}
    </div>
  );
}
